import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { IndianRupee, Plus, Pencil, Trash2, Download, Wallet, Smartphone, CreditCard } from "lucide-react";

const MANAGERS = ["Abhishek Singh", "Om Prakash Sahu", "Fatima Shabnam"];

const empty = {
  date: "", cash_sale: 0, upi_sale: 0, card_sale: 0,
  opening_cash: 0, closing_cash: 0, notes: "", manager: "",
};

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

function StatTile({ label, value, sub, icon: Icon, dark, testId }) {
  return (
    <div className={`p-6 fade-up ${dark ? "bg-primary text-primary-foreground" : "bg-white border border-border"}`} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className={`text-xs uppercase tracking-widest ${dark ? "opacity-70" : "text-muted-foreground"}`}>{label}</div>
          <div className="mt-3 font-serif text-3xl tracking-tight">{value}</div>
          {sub && <div className={`mt-1 text-xs ${dark ? "opacity-70" : "text-muted-foreground"}`}>{sub}</div>}
        </div>
        {Icon && <Icon className="w-5 h-5 opacity-80" aria-hidden="true" />}
      </div>
    </div>
  );
}

export default function DailySalesPage() {
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const isManager = Boolean(user?.manager_name);

  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + "01";

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(today);
  const [managerFilter, setManagerFilter] = useState("all");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (managerFilter !== "all") params.manager = managerFilter;
      const [{ data: list }, { data: sum }] = await Promise.all([
        api.get("/daily-sales", { params }),
        api.get("/daily-sales/summary", { params: { start_date: startDate, end_date: endDate } }),
      ]);
      setRows(list);
      setSummary(sum);
    } finally { setLoading(false); }
  }, [startDate, endDate, managerFilter]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, date: today, manager: isManager ? user?.manager_name : "" });
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      date: r.date,
      cash_sale: r.cash_sale, upi_sale: r.upi_sale, card_sale: r.card_sale,
      opening_cash: r.opening_cash, closing_cash: r.closing_cash,
      notes: r.notes || "", manager: r.manager || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        date: form.date,
        cash_sale: parseFloat(form.cash_sale) || 0,
        upi_sale: parseFloat(form.upi_sale) || 0,
        card_sale: parseFloat(form.card_sale) || 0,
        opening_cash: parseFloat(form.opening_cash) || 0,
        closing_cash: parseFloat(form.closing_cash) || 0,
        notes: form.notes || "",
      };
      if (isOwner) body.manager = form.manager;

      if (editing) {
        await api.put(`/daily-sales/${editing.id}`, body);
        toast.success("Sale updated");
      } else {
        await api.post("/daily-sales", body);
        toast.success("Sale saved");
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const doDelete = async (r) => {
    if (!window.confirm(`Delete sale entry for ${r.date} by ${r.manager}?`)) return;
    try {
      await api.delete(`/daily-sales/${r.id}`);
      toast.success("Removed");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const downloadMonthly = async () => {
    setDownloading(true);
    try {
      const [year, month] = (endDate || today).split("-").map(Number);
      const res = await api.get("/daily-sales/monthly-pdf", {
        params: { year, month },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `royal-wow-sales-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded sales PDF for ${year}-${String(month).padStart(2, "0")}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Download failed");
    } finally { setDownloading(false); }
  };

  const preview = useMemo(() => {
    const cash = parseFloat(form.cash_sale) || 0;
    const upi = parseFloat(form.upi_sale) || 0;
    const card = parseFloat(form.card_sale) || 0;
    const opening = parseFloat(form.opening_cash) || 0;
    const closing = parseFloat(form.closing_cash) || 0;
    const total = cash + upi + card;
    const expected = opening + cash;
    const variance = closing - expected;
    return { total, expected, variance };
  }, [form]);

  return (
    <div data-testid="sales-page">
      <div className="fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Cash counter</p>
          <h1 className="font-serif text-3xl sm:text-5xl tracking-tight mt-2 flex items-center gap-3">
            Daily Sales <IndianRupee className="w-8 h-8 text-primary" aria-hidden="true" />
          </h1>
          <p className="mt-3 text-muted-foreground">Cash · UPI · Card takings, plus the drawer float for each shift.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={downloadMonthly}
            disabled={downloading}
            data-testid="download-monthly-sales-btn"
            variant="outline"
            className="rounded-full"
          >
            <Download className="w-4 h-4 mr-2" aria-hidden="true" />
            {downloading ? "Preparing…" : "Monthly PDF"}
          </Button>
          {(isManager || isOwner) && (
            <Button onClick={openNew} data-testid="new-sale-btn" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-2" aria-hidden="true" /> New entry
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">From</Label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="sales-filter-start" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">To</Label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="sales-filter-end" />
        </div>
        <div className="sm:col-span-2 lg:col-span-1">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Manager</Label>
          <Select value={managerFilter} onValueChange={setManagerFilter}>
            <SelectTrigger data-testid="sales-filter-manager"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All managers</SelectItem>
              {MANAGERS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile
          testId="sales-stat-total"
          label="Grand total"
          value={fmt(summary?.grand_total)}
          sub={`${summary?.entries ?? 0} entries`}
          dark
        />
        <StatTile testId="sales-stat-cash" label="Cash" value={fmt(summary?.cash_sale)} icon={Wallet} />
        <StatTile testId="sales-stat-upi" label="UPI" value={fmt(summary?.upi_sale)} icon={Smartphone} />
        <StatTile testId="sales-stat-card" label="Card" value={fmt(summary?.card_sale)} icon={CreditCard} />
      </div>

      {/* By manager */}
      {summary?.by_manager?.length > 0 && (
        <div className="mt-6 bg-white border border-border p-6 fade-up">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Split by manager</div>
          <table className="w-full text-sm" data-testid="sales-by-manager">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                <th className="pb-3 font-normal">Manager</th>
                <th className="pb-3 font-normal text-right">Cash</th>
                <th className="pb-3 font-normal text-right">UPI</th>
                <th className="pb-3 font-normal text-right">Card</th>
                <th className="pb-3 font-normal text-right">Total</th>
                <th className="pb-3 font-normal text-right">Entries</th>
              </tr>
            </thead>
            <tbody>
              {summary.by_manager.map((m) => (
                <tr key={m.manager} className="border-t border-border">
                  <td className="py-3 font-medium">{m.manager}</td>
                  <td className="py-3 text-right">{fmt(m.cash)}</td>
                  <td className="py-3 text-right">{fmt(m.upi)}</td>
                  <td className="py-3 text-right">{fmt(m.card)}</td>
                  <td className="py-3 text-right font-serif text-lg text-primary">{fmt(m.total)}</td>
                  <td className="py-3 text-right text-muted-foreground">{m.entries}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Entries list */}
      <div className="mt-6 bg-white border border-border">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">All entries</div>
          <div className="text-xs text-muted-foreground">{rows.length} row{rows.length === 1 ? "" : "s"}</div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-14 text-center">
            <p className="font-serif text-2xl">No sales logged in this range.</p>
            <p className="text-muted-foreground mt-2">Tap "New entry" to record the day's takings.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]" data-testid="sales-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <th className="px-4 py-3 font-normal">Date</th>
                  <th className="px-4 py-3 font-normal">Manager</th>
                  <th className="px-4 py-3 font-normal text-right">Cash</th>
                  <th className="px-4 py-3 font-normal text-right">UPI</th>
                  <th className="px-4 py-3 font-normal text-right">Card</th>
                  <th className="px-4 py-3 font-normal text-right">Total</th>
                  <th className="px-4 py-3 font-normal text-right">Open</th>
                  <th className="px-4 py-3 font-normal text-right">Close</th>
                  <th className="px-4 py-3 font-normal text-right">Variance</th>
                  <th className="px-4 py-3 font-normal"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const canEdit = isOwner || (isManager && r.manager === user?.manager_name);
                  return (
                    <tr key={r.id} className="border-t border-border" data-testid={`sale-row-${r.id}`}>
                      <td className="px-4 py-3">{r.date}</td>
                      <td className="px-4 py-3 font-medium">{r.manager}</td>
                      <td className="px-4 py-3 text-right">{fmt(r.cash_sale)}</td>
                      <td className="px-4 py-3 text-right">{fmt(r.upi_sale)}</td>
                      <td className="px-4 py-3 text-right">{fmt(r.card_sale)}</td>
                      <td className="px-4 py-3 text-right font-serif text-lg text-primary">{fmt(r.total_sale)}</td>
                      <td className="px-4 py-3 text-right text-muted-foreground">{fmt(r.opening_cash)}</td>
                      <td className="px-4 py-3 text-right text-muted-foreground">{fmt(r.closing_cash)}</td>
                      <td className={`px-4 py-3 text-right ${Math.abs(r.variance) < 0.01 ? "text-emerald-700" : r.variance < 0 ? "text-red-600" : "text-amber-700"}`}>
                        {fmt(r.variance)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canEdit && (
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-sale-${r.id}`}>
                              <Pencil className="w-4 h-4" aria-hidden="true" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => doDelete(r)} data-testid={`delete-sale-${r.id}`}>
                              <Trash2 className="w-4 h-4 text-destructive" aria-hidden="true" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="sale-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl tracking-tight">
              {editing ? "Edit sale" : "New sale entry"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sd">Date *</Label>
                <Input id="sd" data-testid="sale-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              {isOwner ? (
                <div>
                  <Label>Manager *</Label>
                  <Select value={form.manager} onValueChange={(v) => setForm({ ...form, manager: v })}>
                    <SelectTrigger data-testid="sale-manager"><SelectValue placeholder="Pick a manager" /></SelectTrigger>
                    <SelectContent>
                      {MANAGERS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label>Manager</Label>
                  <Input value={user?.manager_name || ""} disabled data-testid="sale-manager-locked" />
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="cash">Cash (₹)</Label>
                <Input id="cash" data-testid="sale-cash" type="number" min="0" step="0.01" value={form.cash_sale} onChange={(e) => setForm({ ...form, cash_sale: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="upi">UPI (₹)</Label>
                <Input id="upi" data-testid="sale-upi" type="number" min="0" step="0.01" value={form.upi_sale} onChange={(e) => setForm({ ...form, upi_sale: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="card">Card (₹)</Label>
                <Input id="card" data-testid="sale-card" type="number" min="0" step="0.01" value={form.card_sale} onChange={(e) => setForm({ ...form, card_sale: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="open">Opening drawer cash (₹)</Label>
                <Input id="open" data-testid="sale-open" type="number" min="0" step="0.01" value={form.opening_cash} onChange={(e) => setForm({ ...form, opening_cash: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="close">Closing drawer cash (₹)</Label>
                <Input id="close" data-testid="sale-close" type="number" min="0" step="0.01" value={form.closing_cash} onChange={(e) => setForm({ ...form, closing_cash: e.target.value })} />
              </div>
            </div>

            <div>
              <Label htmlFor="snotes">Notes</Label>
              <Textarea id="snotes" data-testid="sale-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Shift notes, discrepancies, refunds…" />
            </div>

            <div className="bg-secondary p-4 border border-border grid grid-cols-3 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total sales</div>
                <div className="font-serif text-2xl text-primary" data-testid="sale-total-preview">{fmt(preview.total)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Expected cash</div>
                <div className="font-serif text-2xl">{fmt(preview.expected)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Variance</div>
                <div className={`font-serif text-2xl ${Math.abs(preview.variance) < 0.01 ? "text-emerald-700" : preview.variance < 0 ? "text-red-600" : "text-amber-700"}`} data-testid="sale-variance-preview">
                  {fmt(preview.variance)}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="save-sale-btn" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {saving ? "Saving…" : editing ? "Save" : "Log sale"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
